import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import {
  ESIGN_SECTION_ANCHOR_ATTR,
  ESIGN_SECTION_TITLE_ATTR,
} from '@/constants/esignature';

interface SectionEntry {
  id: string;
  title: string;
}

/**
 * The composer hides a section by wrapping it in a `hidden` Box, so the
 * attribute is the reliable signal — `offsetParent` needs real layout, which
 * neither jsdom nor a detached subtree provides.
 */
const isVisible = (element: HTMLElement) =>
  element.closest('[hidden]') === null;

/**
 * Section rail for step 2.
 *
 * Which sections exist depends on answers inside the composer — the company
 * block appears only when someone from the employer signs, the copy-recipient
 * block only when there are CC recipients. Rather than duplicate those rules,
 * the rail reads the sections the composer actually rendered and re-reads them
 * whenever the composer's subtree changes.
 *
 * kula's `Tabs` in its `side` variant is the closest primitive, but it owns the
 * panels it switches between; here the content is one continuous form that the
 * composer renders, and the rail only scrolls within it. Worth raising upstream
 * as a nav-only mode for `Tabs variant="side"` in `@kula-ai/components`.
 */
export const EsignDetailsStep = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [sections, setSections] = useState<SectionEntry[]>([]);
  const [activeId, setActiveId] = useState<string>();

  const readSections = useCallback(() => {
    const root = scrollRef.current;
    if (!root) return;
    const found = Array.from(
      root.querySelectorAll<HTMLElement>(`[${ESIGN_SECTION_ANCHOR_ATTR}]`)
    )
      .filter(isVisible)
      .map((element) => ({
        id: element.getAttribute(ESIGN_SECTION_ANCHOR_ATTR) as string,
        title: element.getAttribute(ESIGN_SECTION_TITLE_ATTR) ?? '',
      }));

    setSections((current) => {
      const same =
        current.length === found.length &&
        current.every(
          (entry, index) =>
            entry.id === found[index].id && entry.title === found[index].title
        );
      return same ? current : found;
    });
  }, []);

  useEffect(() => {
    readSections();
    const root = scrollRef.current;
    if (!root) return;
    // The composer toggles sections in response to form values, so a one-shot
    // read after mount would leave the rail stale the moment the recruiter
    // answers "does anyone from the employer need to sign?".
    const observer = new MutationObserver(readSections);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'style', 'class'],
    });
    return () => observer.disconnect();
  }, [readSections]);

  const handleScroll = useCallback(() => {
    const root = scrollRef.current;
    if (!root || sections.length === 0) return;
    const top = root.getBoundingClientRect().top;
    // The section whose heading is closest to the top of the pane without
    // having scrolled past it.
    let current = sections[0].id;
    sections.forEach((section) => {
      const element = root.querySelector<HTMLElement>(
        `[${ESIGN_SECTION_ANCHOR_ATTR}="${section.id}"]`
      );
      if (element && element.getBoundingClientRect().top - top <= 24) {
        current = section.id;
      }
    });
    setActiveId(current);
  }, [sections]);

  // Derived rather than synced from an effect: before the first scroll there is
  // no active id yet, and a section can disappear when the composer hides it.
  const selectedId =
    activeId && sections.some((section) => section.id === activeId)
      ? activeId
      : sections[0]?.id;

  const scrollTo = (id: string) => {
    const root = scrollRef.current;
    const element = root?.querySelector<HTMLElement>(
      `[${ESIGN_SECTION_ANCHOR_ATTR}="${id}"]`
    );
    if (!root || !element) return;
    root.scrollTo({
      top: element.offsetTop - root.offsetTop,
      behavior: 'smooth',
    });
    setActiveId(id);
  };

  return (
    <Flex flex="1" minH={0} overflow="hidden">
      {sections.length > 1 && (
        <Flex
          flexDir="column"
          w="198px"
          flexShrink={0}
          gap="4px"
          p="16px 8px 16px 24px"
          borderRight="1px solid"
          borderColor="border-color"
          data-test-id="esign-details-rail"
        >
          {sections.map((section) => {
            const selected = section.id === selectedId;
            return (
              <Flex
                key={section.id}
                as="button"
                type="button"
                align="center"
                h="36px"
                px="12px"
                borderRadius="6px"
                cursor="pointer"
                textAlign="left"
                bg={selected ? 'primary-25' : 'transparent'}
                _hover={{ bg: selected ? 'primary-25' : 'secondary-25' }}
                onClick={() => scrollTo(section.id)}
                aria-current={selected ? 'true' : undefined}
              >
                <Text
                  variant="p"
                  color={selected ? 'primary' : 'subtle'}
                  truncate
                >
                  {section.title}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      )}
      <Box
        ref={scrollRef}
        flex="1"
        minW={0}
        minH={0}
        overflowY="auto"
        p="16px 24px"
        onScroll={handleScroll}
        data-test-id="esign-details-body"
      >
        {children}
      </Box>
    </Flex>
  );
};
